import { ApolloError, NetworkStatus, useQuery } from '@apollo/client';
import { Query } from 'graphql/generated/graphqlTypes';
import { GET_ALL_VISITORS_CARDS } from 'graphql/queries';
import {
  GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS,
  GET_ALL_VISITORS_FOR_TENANT,
  GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';

export const refetchQueries = [
  { query: GET_ALL_VISITORS_FOR_TENANT },
  { query: GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS },
  { query: GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS }
];

const useAllVisitors = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();

  const {
    loading: visitorsLoading,
    data: visitorsData,
    error: visitorsError,
    networkStatus: visitorsNetworkStatus
  } = useQuery<Pick<Query, 'getVisitors'>>(GET_ALL_VISITORS_WITHOUT_NESTED_RESOLVERS, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (visitorsError) handleFetchError(translation.users_error_fetching);
  }, [visitorsError]);

  const {
    loading: groupsLoading,
    data: groupsData,
    error: groupsError,
    networkStatus: groupsNetworkStatus
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (groupsError) handleFetchError(translation.user_groups_error_fetching);
  }, [groupsError]);

  const visitorsPending = (visitorsLoading && !visitorsData) || (groupsLoading && !groupsData);

  // Keep the combined view in a refetching state until all background requests have finished.
  // Some queries may already be refreshed while others are still in progress.
  const isRefetchingAllVisitors =
    !visitorsPending &&
    ((!!visitorsData && visitorsNetworkStatus !== NetworkStatus.ready) ||
      (!!groupsData && groupsNetworkStatus !== NetworkStatus.ready));

  useRegisterBackgroundRefetch(isRefetchingAllVisitors);

  const {
    loading: visitorsComposedLoading,
    data: allVisitorsData,
    error: visitorsComposedError
  } = useQuery<Pick<Query, 'getVisitors'>>(GET_ALL_VISITORS_FOR_TENANT, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: visitorsPending
  });

  useEffect(() => {
    if (visitorsComposedError) handleFetchError(translation.users_error_fetching);
  }, [visitorsComposedError]);

  const handleCardsError = (error: ApolloError) => {
    handleFetchError(translation.nfc_cards_error_fetching);
    console.error(error);
  };

  const {
    loading: cardsLoading,
    data: cardsData,
    error: cardsError,
    networkStatus: cardsNetworkStatus
  } = useQuery<Pick<Query, 'getNfcCards'>>(GET_ALL_VISITORS_CARDS_WITHOUT_NESTED_RESOLVERS, {
    notifyOnNetworkStatusChange: true
  });

  useEffect(() => {
    if (cardsError) handleCardsError(cardsError);
  }, [cardsError]);

  const cardsPending = cardsLoading && !cardsData;

  const isRefetchingCards = !!cardsData && cardsNetworkStatus !== NetworkStatus.ready;

  useRegisterBackgroundRefetch(isRefetchingCards);

  const {
    loading: cardsComposedLoading,
    data: allVisitorsCardsData,
    error: cardsComposedError
  } = useQuery<Pick<Query, 'getNfcCards'>>(GET_ALL_VISITORS_CARDS, {
    fetchPolicy: 'cache-only',
    returnPartialData: true,
    skip: cardsPending
  });

  useEffect(() => {
    if (cardsComposedError) handleCardsError(cardsComposedError);
  }, [cardsComposedError]);

  return {
    allVisitors: allVisitorsData?.getVisitors || [],
    loadingAllVisitors: visitorsPending || visitorsComposedLoading,
    isRefetchingAllVisitors,
    allVisitorsCards: allVisitorsCardsData?.getNfcCards || [],
    loadingAllVisitorsCards: cardsPending || cardsComposedLoading,
    isRefetchingCards
  };
};

export default useAllVisitors;
